import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../../backend/firebaseConfig";

import "../../css/StaffHoliday/StaffHoliday.css";
import "../../css/Marketing/FinancialPromotions/FinancialPromotions.css";
import Sidebar from "../DesignSystem/Sidebar";
import Breadcrumb from "../DesignSystem/Breadcrumb/Breadcrumb";
import PrimaryButton from "../DesignSystem/Buttons/PrimaryButton";

import { getCachedBankHolidays } from "./services/staffHolidayBankHolidayService";

import {
  fetchStaffHolidayDirectory,
  parseHolidayExcelFile,
  uploadHolidayExcelImport,
} from "./utils/staffHolidayExcelImport";

const StaffHolidayImportPage = () => {
  const navigate = useNavigate();
  const currentUserEmail = auth.currentUser?.email || "";

  const [file, setFile] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [staffDirectory, setStaffDirectory] = useState([]);
  const [manualMap, setManualMap] = useState({});
  const [parsedImport, setParsedImport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [uploadSummary, setUploadSummary] = useState(null);

  useEffect(() => {
    const loadStaff = async () => {
      try {
        const staff = await fetchStaffHolidayDirectory();
        setStaffDirectory(staff);
      } catch (error) {
        // console.error("Failed to load staff directory:", error);
      }
    };

    loadStaff();
  }, []);

  const staffOptions = useMemo(() => {
    return staffDirectory
      .map((staff) => ({
        email: staff.email || staff.id,
        label:
          staff.displayName ||
          staff.name ||
          [staff.firstName, staff.lastName].filter(Boolean).join(" ") ||
          staff.email ||
          staff.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [staffDirectory]);

  const handleParse = async () => {
    if (!file) {
      setMessage("Please select an Excel file first.");
      return;
    }

    // console.log("[StaffHolidayImport] Parse started", {
    //   fileName: file.name,
    //   selectedYear: Number(year),
    //   manualMapCount: Object.keys(manualMap || {}).length,
    // });

    setLoading(true);
    setMessage("");

    try {
      const bankHolidays = await getCachedBankHolidays({
        region: "england-and-wales",
        year: Number(year),
      });

      const parsed = await parseHolidayExcelFile({
        file,
        year: Number(year),
        staffDirectory,
        manualNameToEmailMap: manualMap,
        bankHolidays,
      });

      // console.log("[StaffHolidayImport] Parse completed", {
      //   fileName: parsed.fileName,
      //   year: parsed.year,
      //   staffMatched: parsed.totals?.staffMatched,
      //   events: parsed.totals?.events,
      //   unmatched: parsed.totals?.unmatched,
      //   skipped: parsed.totals?.skipped,
      // });

      setParsedImport(parsed);
      setUploadSummary(null);

      if (parsed.unmatched.length > 0) {
        setMessage(
          `${parsed.unmatched.length} staff name(s) are unmatched. Upload will continue with matched staff only.`,
        );
      } else {
        setMessage("Excel file parsed successfully.");
      }
    } catch (error) {
      // console.error("Failed to parse holiday Excel:", error);
      setMessage(error.message || "Failed to parse Excel file.");
    } finally {
      setLoading(false);
    }
  };

  const handleManualMatchChange = (excelName, staffEmail) => {
    setManualMap((prev) => ({
      ...prev,
      [excelName]: staffEmail,
    }));
  };

  const handleUpload = async () => {
    if (!parsedImport) {
      setMessage("Please parse the Excel file first.");
      return;
    }

    // console.log("[StaffHolidayImport] Upload started", {
    //   fileName: parsedImport.fileName,
    //   year: parsedImport.year,
    //   staffRows: parsedImport.staffRows?.length || 0,
    //   events: parsedImport.events?.length || 0,
    //   unmatched: parsedImport.unmatched?.length || 0,
    // });

    setUploading(true);
    setMessage("");
    setUploadSummary(null);

    try {
      const result = await uploadHolidayExcelImport({
        parsedImport,
        importedBy: currentUserEmail,
        updateStaffEntitlement: false,
        allowUnmatched: true,
      });

      // console.log("[StaffHolidayImport] Upload completed", {
      //   result,
      //   importedBy: currentUserEmail,
      // });

      const sampleStaffEmails = Array.from(
        new Set(
          parsedImport.staffRows
            .map((row) => row.staffEmail)
            .filter(Boolean)
            .slice(0, 5),
        ),
      );

      setUploadSummary({
        year: parsedImport.year,
        fileName: parsedImport.fileName,
        staffUploaded: result.staffUploaded,
        eventsUploaded: result.eventsUploaded,
        unmatchedSkipped: parsedImport.unmatched.length,
        sampleStaffEmails,
      });

      setMessage(
        `Upload complete. ${result.staffUploaded} staff balance(s) and ${result.eventsUploaded} leave event(s) uploaded. ${parsedImport.unmatched.length} unmatched staff name(s) skipped.`,
      );
    } catch (error) {
      // console.error("Failed to upload holiday import:", error);
      setMessage(error.message || "Failed to upload holiday data.");
    } finally {
      setUploading(false);
    }
  };

  const handleOpenTracker = () => {
    if (!uploadSummary?.year) {
      // console.log("[StaffHolidayImport] Open tracker without year summary");
      navigate("/staff-holiday/tracker");
      return;
    }

    // console.log("[StaffHolidayImport] Open tracker with year", {
    //   year: uploadSummary.year,
    // });

    navigate(`/staff-holiday/tracker?year=${uploadSummary.year}`);
  };

  return (
    <div className="Main-Page">
      <Sidebar />

      <div className="Main-Container">
        <main className="staff-holiday-page">
          <Breadcrumb
            items={[
              { label: "All Staff", to: "/Department/AllStaff" },
              { label: "My Holiday", to: "/staff-holiday" },
              { label: "Import Holiday Data" },
            ]}
          />

          <div className="staff-holiday-header">
            <div>
              <p className="staff-holiday-eyebrow">Staff Holiday</p>
              <h1 className="FP_H1 staff-holiday-title">
                Import Existing Holiday Data
              </h1>
              <p className="FP_Subtitle">
                Upload your existing Holiday Excel file and import approved
                leave events and yearly balances.
              </p>
            </div>
          </div>

          <div className="staff-holiday-card staff-holiday-form">
            <div className="staff-holiday-form-row">
              <label>
                Holiday year
                <input
                  type="number"
                  value={year}
                  onChange={(event) => setYear(event.target.value)}
                />
              </label>

              <label>
                Excel file
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(event) => {
                    setFile(event.target.files?.[0] || null);
                    setParsedImport(null);
                    setUploadSummary(null);
                    setMessage("");
                  }}
                />
              </label>
            </div>

            <div className="staff-holiday-actions">
              <button type="button" onClick={handleParse} disabled={loading}>
                {loading ? "Reading Excel..." : "Preview Import"}
              </button>

              <button
                type="button"
                className="staff-holiday-primary-button"
                onClick={handleUpload}
                disabled={uploading || !parsedImport}
              >
                {uploading ? "Uploading..." : "Upload to Firebase"}
              </button>
            </div>

            {message && <div className="staff-holiday-message">{message}</div>}

            {uploadSummary && (
              <div className="staff-holiday-upload-summary">
                <h3>Upload Verification</h3>

                <div className="staff-holiday-upload-summary-grid">
                  <div>
                    <span>File</span>
                    <strong>{uploadSummary.fileName}</strong>
                  </div>

                  <div>
                    <span>Year</span>
                    <strong>{uploadSummary.year}</strong>
                  </div>

                  <div>
                    <span>Staff Uploaded</span>
                    <strong>{uploadSummary.staffUploaded}</strong>
                  </div>

                  <div>
                    <span>Events Uploaded</span>
                    <strong>{uploadSummary.eventsUploaded}</strong>
                  </div>
                </div>

                {uploadSummary.sampleStaffEmails.length > 0 && (
                  <div className="staff-holiday-upload-summary-list">
                    <span>Sample staff records</span>
                    <ul>
                      {uploadSummary.sampleStaffEmails.map((email) => (
                        <li key={email}>{email}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="staff-holiday-actions">
                  <button
                    type="button"
                    className="staff-holiday-primary-button"
                    onClick={handleOpenTracker}
                  >
                    Open Tracker for {uploadSummary.year}
                  </button>
                </div>
              </div>
            )}
          </div>

          {parsedImport && (
            <div className="staff-holiday-card">
              <h2>Import Preview</h2>

              <div className="staff-holiday-stats">
                <div className="staff-holiday-stat-card">
                  <span>Year</span>
                  <strong>{parsedImport.year}</strong>
                </div>

                <div className="staff-holiday-stat-card">
                  <span>Matched staff</span>
                  <strong>{parsedImport.totals.staffMatched}</strong>
                </div>

                <div className="staff-holiday-stat-card">
                  <span>Leave events</span>
                  <strong>{parsedImport.totals.events}</strong>
                </div>

                <div className="staff-holiday-stat-card">
                  <span>Unmatched</span>
                  <strong>{parsedImport.totals.unmatched}</strong>
                </div>
              </div>

              {parsedImport.unmatched.length > 0 && (
                <div className="staff-holiday-import-section">
                  <h3>Match Staff Names</h3>

                  {parsedImport.unmatched.map((item) => (
                    <div
                      key={item.excelName}
                      className="staff-holiday-import-match-row"
                    >
                      <strong>{item.excelName}</strong>

                      <select
                        value={manualMap[item.excelName] || ""}
                        onChange={(event) =>
                          handleManualMatchChange(
                            item.excelName,
                            event.target.value,
                          )
                        }
                      >
                        <option value="">Select staff member</option>

                        {staffOptions.map((staff) => (
                          <option key={staff.email} value={staff.email}>
                            {staff.label} - {staff.email}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}

                  <PrimaryButton type="button" onClick={handleParse}>
                    Re-check Matches
                  </PrimaryButton>
                </div>
              )}

              {parsedImport.skipped.length > 0 && (
                <div className="staff-holiday-import-section">
                  <h3>Skipped Bank Holidays</h3>

                  {parsedImport.skipped.map((item) => (
                    <div
                      key={`${item.staffEmail}-${item.date}`}
                      className="staff-holiday-excluded-row"
                    >
                      <span>
                        {item.excelName} - {item.date}
                      </span>
                      <strong>{item.reason}</strong>
                    </div>
                  ))}
                </div>
              )}

              <div className="staff-holiday-tracker-scroll">
                <table className="staff-holiday-tracker-table">
                  <thead>
                    <tr>
                      <th>Staff</th>
                      <th>Email</th>
                      <th>Department</th>
                      <th>Events</th>
                      <th>Event Days</th>
                      <th>Entitlement</th>
                      <th>Taken</th>
                      <th>Remaining</th>
                    </tr>
                  </thead>

                  <tbody>
                    {parsedImport.staffRows.map((row) => (
                      <tr key={row.staffEmail}>
                        <td>{row.staffName}</td>
                        <td>{row.staffEmail}</td>
                        <td>{row.departmentName || "-"}</td>
                        <td>{row.eventCount}</td>
                        <td>{row.eventDays}</td>
                        <td>{row.balance.entitlement}</td>
                        <td>{row.balance.annualLeaveTaken}</td>
                        <td>{row.balance.remaining}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default StaffHolidayImportPage;
